import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import LegalModal from './LegalModal';
import 'react-toastify/dist/ReactToastify.css';

interface FormErrors {
  firstName?: string;
  lastName?: string;
  childFirstName?: string;
  childLastName?: string;
  relationToStudent?: string;
  email?: string;
  username?: string;
  referralCode?: string;
  password?: string;
  confirmPassword?: string;
  phone?: string;
  otp?: string;
  general?: string;
}

type EnrollmentType = 'STUDENT' | 'TEACHER' | 'SCHOOL' | 'INSTITUTE';

const COUNTRY_CODE_OPTIONS = [
  { code: '+1', label: 'US (+1)', regions: ['US', 'CA'] },
  { code: '+44', label: 'UK (+44)', regions: ['GB'] },
  { code: '+61', label: 'Australia (+61)', regions: ['AU'] },
  { code: '+65', label: 'Singapore (+65)', regions: ['SG'] },
  { code: '+91', label: 'India (+91)', regions: ['IN'] },
  { code: '+971', label: 'UAE (+971)', regions: ['AE'] },
];

const detectDefaultCountryCode = (): string => {
  const locale = String(navigator.language || '').toUpperCase();
  const regionMatch = locale.match(/-([A-Z]{2})$/);
  const region = regionMatch?.[1];
  if (region) {
    const byRegion = COUNTRY_CODE_OPTIONS.find((item) => item.regions.includes(region));
    if (byRegion) return byRegion.code;
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  if (timezone.startsWith('Asia/Kolkata') || timezone.startsWith('Asia/Calcutta')) return '+91';
  if (timezone.startsWith('America/')) return '+1';
  if (timezone.startsWith('Europe/London')) return '+44';
  if (timezone.startsWith('Australia/')) return '+61';

  return '+1';
};

const toErrorText = (error: any, fallback: string): string => {
  const flattenValidationArray = (items: any[]): string[] =>
    items
      .flatMap((item) => {
        if (typeof item === 'string') return [item];
        if (item?.constraints && typeof item.constraints === 'object') {
          return Object.values(item.constraints).map((value) => String(value));
        }
        if (Array.isArray(item?.children) && item.children.length) {
          return flattenValidationArray(item.children);
        }
        if (item?.message) return [String(item.message)];
        return [];
      })
      .filter(Boolean);

  const payload = error?.response?.data?.message ?? error?.message;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (Array.isArray(payload)) {
    const lines = flattenValidationArray(payload);
    return lines.length ? lines.join(', ') : fallback;
  }
  if (payload && typeof payload === 'object') {
    if (typeof payload.message === 'string' && payload.message.trim()) return payload.message;
    if (Array.isArray(payload.message)) {
      const lines = flattenValidationArray(payload.message);
      return lines.length ? lines.join(', ') : fallback;
    }
    if (typeof payload.error === 'string' && payload.error.trim()) return payload.error;
    return fallback;
  }
  return fallback;
};



const RegisterForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    childFirstName: '',
    childLastName: '',
    relationToStudent: '',
    email: '',
    username: '',
    referralCode: '',
    password: '',
    confirmPassword: '',
    enrollmentType: 'STUDENT' as EnrollmentType,
    studentRegistrationMode: 'SELF' as 'SELF' | 'CHILD',
    phone: '',
  });
  const [otp, setOtp] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [registrationIdentifier, setRegistrationIdentifier] = useState('');
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [requiresOtpVerification, setRequiresOtpVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [locationConsent, setLocationConsent] = useState(false);
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);
  const [hasReachedTermsEnd, setHasReachedTermsEnd] = useState(false);
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, verifyRegistrationOtp, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedNextPath = params.get('next') || '';
  const nextPath = requestedNextPath.startsWith('/') && !requestedNextPath.startsWith('//') ? requestedNextPath : '/dashboard';
  
  // Redirect if user is already authenticated
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      navigate(nextPath);
    }
  }, [navigate, nextPath]);

  useEffect(() => {
    setCountryCode(detectDefaultCountryCode());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referralFromLink = String(
      params.get('ref') || params.get('referralCode') || '',
    ).trim().toUpperCase();

    if (!referralFromLink) return;

    setFormData((prev) => ({
      ...prev,
      referralCode: prev.referralCode || referralFromLink,
    }));
  }, [location.search]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const isStudent = formData.enrollmentType === 'STUDENT';
    const isChildRegistration = isStudent && formData.studentRegistrationMode === 'CHILD';
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = isChildRegistration
        ? 'Parent or guardian first name is required'
        : 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = isChildRegistration
        ? 'Parent or guardian last name is required'
        : 'Last name is required';
    }

    if (isChildRegistration && !formData.childFirstName.trim()) {
      newErrors.childFirstName = 'Child first name is required';
    }

    if (isChildRegistration && !formData.relationToStudent.trim()) {
      newErrors.relationToStudent = 'Please specify your relation to the child';
    }
    
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Mobile number is required';
    } else if (!/^[0-9]{6,14}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Enter a valid mobile number (digits only)';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!termsAccepted) {
      newErrors.general = 'Please review and accept the Terms of Service before continuing.';
    } else if (!locationConsent) {
      newErrors.general = 'Please consent to location processing within 20km to continue.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleModalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const node = e.currentTarget;
    if (!node || hasReachedTermsEnd) return;
    const threshold = 12;
    const reachedBottom =
      node.scrollTop + node.clientHeight >= node.scrollHeight - threshold;
    if (reachedBottom) {
      setHasReachedTermsEnd(true);
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const nextValue = name === 'phone' ? value.replace(/\D/g, '') : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue,
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const email = formData.email.trim();
      const resolvedRole = formData.enrollmentType === 'STUDENT' ? 'STUDENT' : 'TEACHER';
      const response = await register({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        username: formData.username.trim(),
        password: formData.password,
        role: resolvedRole,
        phone: `${countryCode}${formData.phone.trim()}`,
        ...(resolvedRole === 'STUDENT'
          ? {
              studentRegistrationMode: formData.studentRegistrationMode,
              childFirstName: formData.childFirstName.trim() || undefined,
              childLastName: formData.childLastName.trim() || undefined,
              relationToStudent: formData.relationToStudent.trim() || undefined,
            }
          : {}),
        ...(formData.referralCode.trim() ? { referralCode: formData.referralCode.trim().toUpperCase() } : {}),
        ...(email ? { email } : {}),
      });
      setRequiresOtpVerification(true);
      setRegistrationIdentifier(response?.identifier || `${countryCode}${formData.phone.trim()}` || formData.email.trim());
      setDevOtp(response?.otp || null);

      toast.success('OTP sent. Please verify to complete registration.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        childFirstName: '',
        childLastName: '',
        relationToStudent: '',
        email: '',
        username: '',
        referralCode: '',
        password: '',
        confirmPassword: '',
        enrollmentType: 'STUDENT',
        studentRegistrationMode: 'SELF',
        phone: '',
      });
    } catch (error: any) {
      const errorMessage = toErrorText(error, 'Failed to create an account. Please try again.');
      setErrors(prev => ({
        ...prev,
        general: errorMessage,
      }));
      
      if (error.response?.data?.errors) {
        // Handle field-specific errors from the backend
        const fieldErrors = error.response.data.errors.reduce((acc: any, err: any) => {
          acc[err.field] = err.message;
          return acc;
        }, {});
        
        setErrors(prev => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      
      toast.error(String(errorMessage), {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setErrors((prev) => ({ ...prev, otp: 'OTP is required' }));
      return;
    }
    try {
      setIsSubmitting(true);
      await verifyRegistrationOtp(registrationIdentifier, otp.trim());
      toast.success('Registration verified successfully.');
      navigate(nextPath);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'OTP verification failed';
      setErrors((prev) => ({ ...prev, otp: String(errorMessage) }));
      toast.error(String(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  
  // Add loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.16),_transparent_26%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.14),_transparent_22%),linear-gradient(180deg,_#f8fafc_0%,_#e0e7ff_100%)] py-12 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-[calc(100vh-6rem)] max-w-md items-center justify-center">
          <div className="w-full rounded-[28px] border border-white/60 bg-white/88 p-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-sm font-medium text-slate-600">Loading registration form...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(79,70,229,0.16),_transparent_26%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.14),_transparent_22%),linear-gradient(180deg,_#f8fafc_0%,_#e0e7ff_100%)]">
      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Left side - Background Image */}
        <div className="hidden lg:block relative w-1/2">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80)',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }}
          >
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0.42)_0%,rgba(67,56,202,0.82)_100%)]"></div>
            <div className="relative h-full flex flex-col justify-center p-12 text-white">
              <span className="inline-flex w-fit rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-100">
                New Member Setup
              </span>
              <h2 className="mt-6 text-5xl font-semibold tracking-tight">Create your space in LocalAlly</h2>
              <p className="mt-4 max-w-xl text-lg text-indigo-100">Start with a clean profile, verify once with OTP, and unlock nearby learning opportunities faster.</p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 rounded-full bg-white/20 p-2">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium">Fast onboarding</h3>
                    <p className="mt-1 text-indigo-100">Choose your account type, verify once, and start using the platform without extra setup.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 rounded-full bg-white/20 p-2">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium">Built for every learning role</h3>
                    <p className="mt-1 text-indigo-100">Students, teachers, schools, and institutes can all enter through one clean registration flow.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 rounded-full bg-white/20 p-2">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium">Verified and secure</h3>
                    <p className="mt-1 text-indigo-100">Mobile-first verification keeps account creation simple while protecting access.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Registration Form */}
        <div className="flex w-full items-start justify-center px-4 py-6 sm:px-6 sm:py-10 lg:w-1/2 lg:px-12">
          <div className="w-full max-w-xl rounded-[26px] border border-white/60 bg-white/88 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.10)] backdrop-blur sm:rounded-[30px] sm:p-8">
            <div className="mb-8">
              <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600">
                Create Account
              </div>
              <h1 className="mt-4 text-[1.85rem] font-semibold tracking-tight text-slate-900 sm:mt-6 sm:text-4xl">
                {requiresOtpVerification ? 'Verify your registration' : 'Join LocalAlly'}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {requiresOtpVerification
                  ? 'Enter the OTP sent to your registered contact to activate your account.'
                  : 'Modern local learning starts with a quick setup. Email is optional, mobile number is required.'}
              </p>
            </div>
            {errors.general && (
              <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-rose-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-rose-700">{errors.general}</p>
                  </div>
                </div>
              </div>
            )}

            {!requiresOtpVerification ? (
            <form className="space-y-6" onSubmit={handleSubmit}>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-slate-700">
                  {formData.enrollmentType === 'STUDENT' && formData.studentRegistrationMode === 'CHILD'
                    ? 'Parent / Guardian first name'
                    : 'First name'} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  autoComplete="given-name"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`block w-full rounded-2xl border ${
                    errors.firstName ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                  } px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                />
                {errors.firstName && (
                  <p className="mt-2 text-sm text-rose-600" id="firstName-error">
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-slate-700">
                  {formData.enrollmentType === 'STUDENT' && formData.studentRegistrationMode === 'CHILD'
                    ? 'Parent / Guardian last name'
                    : 'Last name'} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  autoComplete="family-name"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`block w-full rounded-2xl border ${
                    errors.lastName ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                  } px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  aria-invalid={!!errors.lastName}
                  aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                />
                {errors.lastName && (
                  <p className="mt-2 text-sm text-rose-600" id="lastName-error">
                    {errors.lastName}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="enrollmentType" className="mb-2 block text-sm font-medium text-slate-700">
                  I want to enroll as
                </label>
                <select
                  id="enrollmentType"
                  name="enrollmentType"
                  required
                  value={formData.enrollmentType}
                  onChange={handleChange}
                  className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="STUDENT">Student</option>
                  <option value="TEACHER">Teacher</option>
                  <option value="SCHOOL">School</option>
                  <option value="INSTITUTE">Institute / Tuition Center</option>
                </select>
                <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                  School and institute enrollments currently continue under the teacher account flow.
                </div>
              </div>

              {formData.enrollmentType === 'STUDENT' && (
                <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <label htmlFor="studentRegistrationMode" className="mb-2 block text-sm font-medium text-slate-700">
                    Student registration type
                  </label>
                  <select
                    id="studentRegistrationMode"
                    name="studentRegistrationMode"
                    value={formData.studentRegistrationMode}
                    onChange={handleChange}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="SELF">For myself</option>
                    <option value="CHILD">On behalf of my child</option>
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    Use one verified parent login to manage one or more child student profiles.
                  </p>
                </div>
              )}

              {formData.enrollmentType === 'STUDENT' && formData.studentRegistrationMode === 'CHILD' && (
                <>
                  <div>
                    <label htmlFor="childFirstName" className="mb-2 block text-sm font-medium text-slate-700">
                      Child first name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="childFirstName"
                      id="childFirstName"
                      value={formData.childFirstName}
                      onChange={handleChange}
                      className={`block w-full rounded-2xl border ${
                        errors.childFirstName ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                      } px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    />
                    {errors.childFirstName && <p className="mt-2 text-sm text-rose-600">{errors.childFirstName}</p>}
                  </div>

                  <div>
                    <label htmlFor="childLastName" className="mb-2 block text-sm font-medium text-slate-700">
                      Child last name
                    </label>
                    <input
                      type="text"
                      name="childLastName"
                      id="childLastName"
                      value={formData.childLastName}
                      onChange={handleChange}
                      className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="relationToStudent" className="mb-2 block text-sm font-medium text-slate-700">
                      Your relation to the child <span className="text-rose-500">*</span>
                    </label>
                    <select
                      id="relationToStudent"
                      name="relationToStudent"
                      value={formData.relationToStudent}
                      onChange={handleChange}
                      className={`block w-full rounded-2xl border ${
                        errors.relationToStudent ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                      } px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    >
                      <option value="">Select relation</option>
                      <option value="Mother">Mother</option>
                      <option value="Father">Father</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Relative">Relative</option>
                    </select>
                    {errors.relationToStudent && <p className="mt-2 text-sm text-rose-600">{errors.relationToStudent}</p>}
                  </div>
                </>
              )}

              <div className="sm:col-span-2">
                <label htmlFor="username" className="mb-2 block text-sm font-medium text-slate-700">
                  Username <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="username"
                  id="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className={`block w-full rounded-2xl border ${
                    errors.username ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                  } px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  aria-invalid={!!errors.username}
                />
                {errors.username && (
                  <p className="mt-2 text-sm text-rose-600">{errors.username}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                  Email address <span className="text-slate-400">(optional)</span>
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className={`block w-full rounded-2xl border ${
                    errors.email ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                  } px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-rose-600" id="email-error">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="phone" className="mb-2 block text-sm font-medium text-slate-700">
                  Mobile number <span className="text-rose-500">*</span>
                </label>
                <div className="grid gap-3 sm:grid-cols-[140px_minmax(0,1fr)]">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {COUNTRY_CODE_OPTIONS.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    autoComplete="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Mobile number"
                    className={`block w-full rounded-2xl border ${
                      errors.phone ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                    } px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? 'phone-error' : undefined}
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Country code defaults from browser location or time zone.
                </p>
                {errors.phone && (
                  <p className="mt-2 text-sm text-rose-600" id="phone-error">
                    {errors.phone}
                  </p>
                )}
              </div>

              <div className="sm:col-span-1">
                <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">
                  Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`block w-full rounded-2xl border ${
                    errors.password ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                  } px-4 py-3 pr-11 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l18 18" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9.88 4.24A10.94 10.94 0 0112 4c5 0 9.27 3.11 11 8-0.61 1.72-1.68 3.23-3.08 4.4M6.1 6.1C4.36 7.36 3 9.07 2 12c1.73 4.89 6 8 10 8 1.61 0 3.15-.41 4.5-1.13" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s3.5-8 10-8 10 8 10 8-3.5 8-10 8-10-8-10-8z" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-rose-600" id="password-error">
                    {errors.password}
                  </p>
                )}
              </div>

              <div className="sm:col-span-1">
                <label htmlFor="confirmPassword" className="mb-2 block text-sm font-medium text-slate-700">
                  Confirm password <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`block w-full rounded-2xl border ${
                    errors.confirmPassword ? 'border-rose-300 bg-rose-50/70' : 'border-slate-200 bg-slate-50'
                  } px-4 py-3 pr-11 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                  aria-invalid={!!errors.confirmPassword}
                  aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-slate-700"
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l18 18" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9.88 4.24A10.94 10.94 0 0112 4c5 0 9.27 3.11 11 8-0.61 1.72-1.68 3.23-3.08 4.4M6.1 6.1C4.36 7.36 3 9.07 2 12c1.73 4.89 6 8 10 8 1.61 0 3.15-.41 4.5-1.13" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s3.5-8 10-8 10 8 10 8-3.5 8-10 8-10-8-10-8z" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-rose-600" id="confirmPassword-error">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="referralCode" className="mb-2 block text-sm font-medium text-slate-700">
                  Referral code <span className="text-slate-400">(optional)</span>
                </label>
                <input
                  type="text"
                  name="referralCode"
                  id="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  placeholder="Enter referral code"
                  className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm uppercase text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="mt-2 text-xs text-slate-500">
                  If valid, the referring user receives a wallet bonus after your account is verified.
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
              Mobile number is required for verification. Email can be added now or later.
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">Legal Agreement</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    By creating an account, you agree to our Terms of Service and Privacy Policy.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setIsLegalModalOpen(true)}
                      className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      Review Terms & Privacy
                    </button>
                    <Link
                      to="/terms"
                      className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      Full Terms
                    </Link>
                    <Link
                      to="/privacy"
                      className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                    >
                      Privacy Policy
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <label className="flex items-start gap-3 rounded-xl border border-white bg-white p-3 text-sm">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (!e.target.checked) {
                        setHasReachedTermsEnd(false);
                      }
                      setErrors((prev) => ({ ...prev, general: undefined }));
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-slate-700">
                    I accept the Terms of Service and Privacy Policy
                  </span>
                </label>

                <label className="flex items-start gap-3 rounded-xl border border-white bg-white p-3 text-sm">
                  <input
                    type="checkbox"
                    checked={locationConsent}
                    onChange={(e) => {
                      setLocationConsent(e.target.checked);
                      setErrors((prev) => ({ ...prev, general: undefined }));
                    }}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-slate-700">
                    I consent to <span className="text-indigo-600 font-medium">Local</span><span className="text-emerald-600 font-medium">Ally</span> using my location to find tutors within 20km
                  </span>
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !termsAccepted || !locationConsent}
                className={`flex w-full justify-center rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(15,23,42,0.18)] transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  isSubmitting || !termsAccepted || !locationConsent ? 'cursor-not-allowed opacity-70' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </button>
            </div>
            <p className="text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                Sign in
              </Link>
            </p>
            </form>
            ) : (
              <form className="space-y-6" onSubmit={handleVerifyOtp}>
                <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-700">
                  OTP sent to your registered contact for <span className="font-semibold">{registrationIdentifier}</span>.
                  {devOtp ? <div className="mt-1">OTP: <span className="font-semibold">{devOtp}</span></div> : null}
                </div>
                <div>
                  <label htmlFor="otp" className="mb-2 block text-sm font-medium text-slate-700">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="6-digit OTP"
                    maxLength={6}
                  />
                  {errors.otp ? <p className="mt-2 text-sm text-rose-600">{errors.otp}</p> : null}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex w-full justify-center rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(15,23,42,0.18)] transition hover:bg-indigo-700 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal
        isOpen={isLegalModalOpen}
        onClose={() => setIsLegalModalOpen(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setHasReachedTermsEnd(true);
          setIsLegalModalOpen(false);
          setErrors((prev) => ({ ...prev, general: undefined }));
        }}
        hasScrolledToEnd={hasReachedTermsEnd}
        onScroll={handleModalScroll}
      />
    </div>
  );
};

export default RegisterForm;
